/**
 * Shared stage-config loader.
 *
 * Globs all generated GMG_*_<suffix>.json files eagerly so Vite bundles only
 * the files that actually exist on disk at build time.  At runtime the
 * requested stage is resolved, strict-mode / production builds throw on a
 * missing file, and lenient mode (plain `npm start`) logs a warning and falls
 * back to the supplied local value.
 *
 * @template T
 * @param {Record<string, unknown>} modules   - result of import.meta.glob (eager)
 * @param {Record<string, string>}  pathMap   - { Dev: '<rel-path>', Test: ..., Prod: ... }
 * @param {string}                  key       - property to extract from the JSON ('config' | 'appSync')
 * @param {T}                       fallback  - value to use when no stage config is found
 * @param {string}                  label     - human-readable name shown in error messages
 * @returns {T}
 */
export function loadStageConfig(modules, pathMap, key, fallback, label) {
    const requestedStage = import.meta.env.VITE_APP_STAGE;
    const isStrict =
        import.meta.env.VITE_REQUIRE_STAGE_CONFIG === 'true' ||
        import.meta.env.PROD;

    if (!requestedStage) {
        return fallback;
    }

    const filePath = pathMap[requestedStage];
    const moduleData = filePath ? modules[filePath] : undefined;
    const value = moduleData && moduleData[key];

    if (value) {
        return /** @type {T} */ (value);
    }

    const message =
        `Stage mismatch: requested VITE_APP_STAGE="${requestedStage}" but ${label} config is missing for that stage. ` +
        `Generate it: cd dev/schemaGenerator && node start.js GMG_${requestedStage}`;

    if (isStrict) {
        throw new Error(message);
    }

    console.error(message);
    return fallback;
}
